import { drawWithLuck, luckOf, secondChanceFrame, secondChanceRate, type Outcome } from './luck';
import type { CasinoGame, TableOption } from './types';

/**
 * Dice (over/under): a roll from 0.00 to 99.99. Pick a target and whether
 * the roll lands over or under it; the payout is set by the odds, times
 * (1 - EDGE), so every choice has the same edge — a safe bet pays little, a
 * long shot a lot. "Target" opens a picker list (5 to 95).
 */
export const EDGE = 0.02;
export const TARGETS = Array.from({ length: 19 }, (_, i) => (i + 1) * 5);
/** Rolling exactly this (in hundredths) wins the jackpot. */
export const JACKPOT_ROLL = 7777;

export type Direction = 'over' | 'under';

/** Chance to win, from the 10,000 equally likely rolls 0.00-99.99. */
export function winChance(target: number, dir: Direction): number {
  const cut = target * 100;
  return (dir === 'over' ? 9999 - cut : cut) / 10000;
}

export function diceMultiplier(target: number, dir: Direction, edge: number = EDGE): number {
  return (1 - edge) / winChance(target, dir);
}

/** Frames of the roll counter racing before it settles on `roll` (the
 *  passing numbers are picked from the result, so they repeat the same). */
export function rollFrames(roll: number): string[] {
  return [7919, 3571, 2749, 1543, 811].map((k, f) => {
    const shown = (roll * 31 + k * (f + 7)) % 10000;
    return `Rolling...\n\n   ${(shown / 100).toFixed(2)}`;
  });
}

/** A target's results (for the ★'s second-chance rate). */
export function diceOutcomes(target: number, dir: Direction): Outcome[] {
  const w = winChance(target, dir);
  return [
    [w, diceMultiplier(target, dir)],
    [1 - w, 0],
  ];
}

export interface DiceState {
  phase: 'ready' | 'pickTarget';
  target: number;
  dir: Direction;
  /** Last roll in hundredths (0-9999), or null before the first. */
  roll: number | null;
  /** The ★'s luck re-rolled a losing roll. */
  lucky?: boolean;
  lastStake: number;
  returned: number;
}

export const dice: CasinoGame<DiceState> = {
  id: 'dice',
  noDealer: true,
  usesLuck: true,
  name: 'Dice',
  blurb: 'Roll 0-99.99. Pick a target, over or under.',
  baseMinBet: 5,
  baseMaxBet: 100,
  specialPerk: 'Luck: a losing roll is sometimes rolled again.',
  jackpotRule: 'Roll exactly 77.77.',
  lockedHint: '• choose  •• cancel',
  create: () => ({ phase: 'ready', target: 50, dir: 'over', roll: null, lastStake: 0, returned: 0 }),
  // The target picker counts as mid-play: no Bet option, double-tap cancels it.
  locked: (state) => state.phase === 'pickTarget',
  options(state): TableOption[] {
    const edge = EDGE;
    if (state.phase === 'pickTarget') {
      return TARGETS.map((t) => ({ id: `t:${t}`, label: `${t} (x${diceMultiplier(t, state.dir, edge).toFixed(2)})` }));
    }
    return [
      { id: 'roll', label: `Roll (x${diceMultiplier(state.target, state.dir, edge).toFixed(2)})` },
      { id: 'target', label: `Target: ${state.target}` },
      { id: 'dir', label: `Win if: ${state.dir}` },
    ];
  },
  describe(state) {
    const chance = `${(winChance(state.target, state.dir) * 100).toFixed(2)}% to win`;
    if (state.phase === 'pickTarget') return `Roll ${state.dir} the target.\n${chance}`;
    if (state.roll === null) return `Win if the roll is ${state.dir} ${state.target}.\n${chance}`;
    const net = state.returned - state.lastStake;
    return `Rolled ${(state.roll / 100).toFixed(2)}\n${net > 0 ? `${state.lucky ? '★ ' : ''}+$${net}` : `-$${state.lastStake}`}\n\nNext: ${state.dir} ${state.target}\n${chance}`;
  },
  act(state, optionId, env) {
    if (optionId === 'target') return { state: { ...state, phase: 'pickTarget' }, bankroll: env.bankroll };
    if (optionId.startsWith('t:')) return { state: { ...state, phase: 'ready', target: Number(optionId.slice(2)) }, bankroll: env.bankroll };
    if (optionId === 'dir') return { state: { ...state, dir: state.dir === 'over' ? 'under' : 'over' }, bankroll: env.bankroll };
    if (optionId !== 'roll' || state.phase !== 'ready') return { state, bankroll: env.bankroll };
    const cut = state.target * 100;
    const wins = (r: number) => (state.dir === 'over' ? r > cut : r < cut);
    // The ★'s luck may re-roll a losing roll (only the re-roll is shown).
    const q = secondChanceRate(diceOutcomes(state.target, state.dir), luckOf(env));
    const { outcome: roll, lucky, first } = drawWithLuck(env.rng, () => env.rng.int(10000), (r) => !wins(r), q);
    const returned = wins(roll) ? Math.round(env.bet * diceMultiplier(state.target, state.dir)) : 0;
    return {
      state: { ...state, roll, lucky, lastStake: env.bet, returned },
      bankroll: env.bankroll - env.bet + returned,
      settled: { wagered: env.bet, returned },
      jackpot: roll === JACKPOT_ROLL,
      // The counter races, then settles; a ★ second chance shows the losing
      // roll first, then rolls again.
      frames: first === undefined ? rollFrames(roll) : [...rollFrames(first), secondChanceFrame(`Rolled ${(first / 100).toFixed(2)}`), ...rollFrames(roll)],
    };
  },
  gesture(state, gesture, env) {
    if (gesture === 'double' && state.phase === 'pickTarget') return { state: { ...state, phase: 'ready' }, bankroll: env.bankroll };
    return null;
  },
};
